import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshMemberships, hasActiveSubscription } = useUserProfile();
  const [ready, setReady] = useState(false);
  const attemptRef = useRef(0);
  const syncAttempted = useRef(false);

  useEffect(() => {
    if (hasActiveSubscription) {
      setReady(true);
      return;
    }

    let cancelled = false;
    async function poll() {
      // First, directly sync the checkout session to Firestore.
      // This is the most reliable path — doesn't depend on webhooks.
      if (sessionId && !syncAttempted.current) {
        syncAttempted.current = true;
        try {
          await api.post('/billing/sync-checkout-session', { sessionId });
        } catch {
          // Non-critical — fall through to polling
        }
        // Refresh immediately after sync
        if (!cancelled) await refreshMemberships();
      }

      // Poll with retries in case the sync didn't detect the subscription yet
      while (attemptRef.current < 5 && !cancelled) {
        await refreshMemberships();
        attemptRef.current++;
        if (!cancelled) {
          await new Promise((r) => setTimeout(r, 1000 + attemptRef.current * 1000));
        }
      }
      if (!cancelled) setReady(true);
    }
    poll();
    return () => { cancelled = true; };
  }, [hasActiveSubscription, refreshMemberships, sessionId]);

  return (
    <div className="min-h-screen bg-mist flex flex-col items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-6 py-10 max-w-sm w-full flex flex-col items-center gap-5 text-center">
        <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
          <CheckCircle size={28} className="text-green-600" />
        </div>

        <div>
          <h1 className="font-display text-h2 font-semibold text-midnight">
            You're All Set!
          </h1>
          <p className="text-sm text-slate mt-2 leading-relaxed">
            Your subscription is now active. Let's set up your organization so
            you can start creating quotes right away.
          </p>
        </div>

        {ready ? (
          <button
            type="button"
            onClick={() => navigate('/setup', { replace: true })}
            className="btn-primary w-full"
          >
            Set Up Your Organization
          </button>
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate">
            <LoadingSpinner size="sm" />
            Activating your plan...
          </div>
        )}
      </div>
    </div>
  );
}
